import React, { useState } from 'react';
import './DataTraceResults.css';

function DataTraceResults({ data, searchParams, onBack, onNewSearch }) {
  const [activeTab, setActiveTab] = useState('property');

  const tabs = [
    { id: 'property', label: 'Property Details', icon: '🏠' },
    { id: 'ownership', label: 'Ownership', icon: '👥' },
    { id: 'tax', label: 'Tax Info', icon: '💰' },
    { id: 'documents', label: 'Documents', icon: '📜' }
  ];

  const renderPropertyDetails = () => (
    <div className="results-section">
      <div className="section-grid">
        <div className="data-card">
          <h4>Location</h4>
          <div className="data-row">
            <span className="data-label">Property Address</span>
            <span className="data-value">{data.property.address}</span>
          </div>
          <div className="data-row">
            <span className="data-label">City, State, ZIP</span>
            <span className="data-value">{data.property.cityStateZip}</span>
          </div>
          <div className="data-row">
            <span className="data-label">County</span>
            <span className="data-value">{data.property.county}</span>
          </div>
          <div className="data-row">
            <span className="data-label">APN</span>
            <span className="data-value mono">{data.property.apn}</span>
          </div>
        </div>

        <div className="data-card">
          <h4>Legal Description</h4>
          <div className="data-row full">
            <span className="data-value legal-desc">{data.property.legalDescription}</span>
          </div>
        </div>

        <div className="data-card">
          <h4>Property Characteristics</h4>
          <div className="data-row">
            <span className="data-label">Property Type</span>
            <span className="data-value">{data.property.propertyType}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Land Use</span>
            <span className="data-value">{data.property.landUse}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Zoning</span>
            <span className="data-value">{data.property.zoning}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Lot Size</span>
            <span className="data-value">{data.property.lotSize}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Year Built</span>
            <span className="data-value">{data.property.yearBuilt}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Living Area</span>
            <span className="data-value">{data.property.livingArea}</span>
          </div>
          <div className="data-row">
            <span className="data-label">Bedrooms / Baths</span>
            <span className="data-value">{data.property.bedsBaths}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOwnership = () => (
    <div className="results-section">
      <div className="section-grid">
        <div className="data-card full-width">
          <h4>Current Owner</h4>
          <div className="owner-info">
            <div className="owner-primary">
              <span className="owner-icon">👤</span>
              <div>
                <div className="owner-name">{data.ownership.currentOwner.name}</div>
                <div className="owner-vesting">{data.ownership.currentOwner.vesting}</div>
              </div>
            </div>
            <div className="owner-details">
              <div className="data-row">
                <span className="data-label">Mailing Address</span>
                <span className="data-value">{data.ownership.currentOwner.mailingAddress}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Acquisition Date</span>
                <span className="data-value">{data.ownership.currentOwner.acquisitionDate}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Acquisition Price</span>
                <span className="data-value">{data.ownership.currentOwner.acquisitionPrice}</span>
              </div>
              <div className="data-row">
                <span className="data-label">Document Number</span>
                <span className="data-value mono">{data.ownership.currentOwner.documentNumber}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="data-card full-width">
          <h4>Ownership History</h4>
          <div className="history-timeline">
            {data.ownership.history.map((record, index) => (
              <div key={index} className="history-item">
                <div className="history-dot"></div>
                <div className="history-content">
                  <div className="history-date">{record.date}</div>
                  <div className="history-name">{record.owner}</div>
                  <div className="history-details">
                    <span className="history-type">{record.transactionType}</span>
                    <span className="history-price">{record.price}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderTaxInfo = () => {
    // Check if tax data is available (not from instant_recordable_legal)
    const hasTaxData = data.tax.taxYear !== 'N/A - Use TaxSource product';

    if (!hasTaxData) {
      return (
        <div className="results-section">
          <div className="section-grid">
            <div className="data-card full-width">
              <div className="tax-unavailable">
                <div className="unavailable-icon">💰</div>
                <h4>Tax Information Not Available</h4>
                <p>
                  The Instant Recordable Legal product does not include tax information.
                  To retrieve tax data, please use the DataTrace TaxSource product.
                </p>
                <p className="unavailable-note">
                  {data.tax.note || 'Tax information requires separate TaxSource product request'}
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="results-section">
        <div className="section-grid">
          <div className="data-card">
            <h4>Current Tax Year ({data.tax.taxYear})</h4>
            <div className="tax-summary">
              <div className="tax-item">
                <span className="tax-label">Total Tax Amount</span>
                <span className="tax-value large">{data.tax.totalTax}</span>
              </div>
              <div className="tax-status">
                <span className={`status-badge ${data.tax.status.toLowerCase()}`}>
                  {data.tax.status}
                </span>
              </div>
            </div>
            <div className="data-row">
              <span className="data-label">1st Installment</span>
              <span className="data-value">{data.tax.firstInstallment}</span>
            </div>
            <div className="data-row">
              <span className="data-label">2nd Installment</span>
              <span className="data-value">{data.tax.secondInstallment}</span>
            </div>
            <div className="data-row">
              <span className="data-label">Tax Rate Area</span>
              <span className="data-value">{data.tax.taxRateArea}</span>
            </div>
          </div>

          <div className="data-card">
            <h4>Assessed Values</h4>
            <div className="data-row">
              <span className="data-label">Land Value</span>
              <span className="data-value">{data.tax.assessedLand}</span>
            </div>
            <div className="data-row">
              <span className="data-label">Improvement Value</span>
              <span className="data-value">{data.tax.assessedImprovement}</span>
            </div>
            <div className="data-row highlight">
              <span className="data-label">Total Assessed Value</span>
              <span className="data-value">{data.tax.totalAssessed}</span>
            </div>
            <div className="data-row">
              <span className="data-label">Market Value</span>
              <span className="data-value">{data.tax.marketValue}</span>
            </div>
          </div>

          <div className="data-card">
            <h4>Exemptions</h4>
            {data.tax.exemptions && data.tax.exemptions.length > 0 ? (
              <ul className="exemption-list">
                {data.tax.exemptions.map((exemption, index) => (
                  <li key={index}>
                    <span className="exemption-type">{exemption.type}</span>
                    <span className="exemption-amount">{exemption.amount}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-data">No exemptions on record</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDocuments = () => (
    <div className="results-section">
      <div className="section-grid">
        <div className="data-card full-width">
          <h4>Recorded Documents</h4>
          <div className="documents-table">
            <div className="table-header">
              <span>Recording Date</span>
              <span>Document Type</span>
              <span>Document Number</span>
              <span>Grantor</span>
              <span>Grantee</span>
            </div>
            {data.documents.map((doc, index) => (
              <div key={index} className="table-row">
                <span>{doc.recordingDate}</span>
                <span className="doc-type">{doc.documentType}</span>
                <span className="mono">{doc.documentNumber}</span>
                <span>{doc.grantor}</span>
                <span>{doc.grantee}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="data-card">
          <h4>Active Liens</h4>
          {data.liens.length > 0 ? (
            <div className="liens-list">
              {data.liens.map((lien, index) => (
                <div key={index} className="lien-item">
                  <div className="lien-type">{lien.type}</div>
                  <div className="lien-amount">{lien.amount}</div>
                  <div className="lien-holder">{lien.holder}</div>
                  <div className="lien-date">Recorded: {lien.recordedDate}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No active liens found</p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="datatrace-results">
      <div className="results-header">
        <div className="header-left">
          <div>
            <h2>Property Report</h2>
            <p className="results-subtitle">
              DataTrace results for APN: <span className="mono">{searchParams.apn}</span>
            </p>
          </div>
        </div>
        <button className="new-search-button" onClick={onNewSearch}>
          New Search
        </button>
      </div>

      <div className="property-summary">
        <div className="summary-address">
          <span className="summary-icon">🏠</span>
          <div>
            <div className="summary-main">{data.property.address}</div>
            <div className="summary-sub">{data.property.cityStateZip}</div>
          </div>
        </div>
        <div className="summary-stats">
          <div className="stat">
            <span className="stat-label">Owner</span>
            <span className="stat-value">{data.ownership.currentOwner.name}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Assessed Value</span>
            <span className="stat-value">{data.tax.totalAssessed}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Property Type</span>
            <span className="stat-value">{data.property.propertyType}</span>
          </div>
        </div>
      </div>

      <div className="results-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="results-content">
        {activeTab === 'property' && renderPropertyDetails()}
        {activeTab === 'ownership' && renderOwnership()}
        {activeTab === 'tax' && renderTaxInfo()}
        {activeTab === 'documents' && renderDocuments()}
      </div>

      <div className="results-footer">
        <p className="disclaimer">
          Data provided by DataTrace. Information is believed to be accurate but should be independently verified.
          Report generated on {new Date().toLocaleDateString()}.
        </p>
      </div>
    </div>
  );
}

export default DataTraceResults;
